import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import * as routes from 'routes';
import { useTranslation } from 'react-i18next';
import compose from 'helpers/compose';
import { inject, observer } from 'mobx-react';

import CrumbRoute from 'components/CrumbRoute';
import RoleBaseRoute from 'components/RoleBasedRoute/RoleBasedRoute';

import EditAccount from 'pages/Accounts/Edit';
import Accounts from 'pages/Accounts/AccountsList/Accounts';
import AccountDetail from 'pages/Accounts/Detail/index';
import AccountCompleteLayout from 'pages/Accounts/Complete/AccountCompleteLayout';
import DocumentsToBeSigned from 'pages/Accounts/DocumentsToBeSigned/DocumentsToBeSigned';
import Rankings from 'pages/Advisor/Rankings';
import Notes from 'pages/Advisor/Notes';
import Admin from 'pages/Admin/Admin';
import BackOffice from 'pages/BackOffice/BackOffice';
import Page404 from 'pages/Error/Page404';
import { UserRoles } from 'enums';
import { UserStoreKC } from 'stores/userStoreKC';
import { Permission } from 'helpers/permissions';

interface Stores {
  userStore: UserStoreKC;
}

function homepageRedirect(userStore: UserStoreKC) {
  if (!userStore.hasRoles(UserRoles.ADVISOR)) {
    if (userStore.checkPermissions(Permission.ACCESS_ADMIN)) return routes.ADMIN_CONSTANTS;
    if (userStore.checkPermissions(Permission.ACCESS_BACKOFFICE)) return routes.BO_CSV;
  }
  return routes.ACCOUNTS_PAGE;
}

const AppRoutes: React.FC<Stores> = ({ userStore }) => {
  const { t } = useTranslation();

  return (
    <Switch>
      <Route
        exact
        path={routes.HOME_PAGE}
        render={() => <Redirect to={homepageRedirect(userStore)} />}
      />

      <CrumbRoute
        exact
        path={[
          routes.ACCOUNTS_PAGE,
          routes.ACCOUNTS_DRAFTS,
          routes.ACCOUNTS_ACTIVE,
          routes.ACCOUNTS_INACTIVE,
          routes.ACCOUNTS_IMPORTANTDATES,
        ]}
        component={Accounts}
        title={t('navMenu_accounts')}
        permission={Permission.ACCESS_BASIC}
      />

      <RoleBaseRoute
        path={[
          routes.ACCOUNTS_DETAIL(),
          routes.ACCOUNTS_DETAIL_HISTORY(),
          routes.ACCOUNTS_DETAIL_REPORTS(),
          routes.ACCOUNTS_DETAIL_ID(),
          routes.ACCOUNTS_DETAIL_PLAN(),
          routes.ACCOUNTS_DETAIL_DOCUMENTS(),
          routes.ACCOUNTS_DETAIL_FEES(),
          routes.ACCOUNTS_DETAIL_MOVEMENTS(),
          routes.ACCOUNTS_DETAIL_PERSONAL(),
        ]}
        component={AccountDetail}
        permission={Permission.ACCESS_BASIC}
      />

      <RoleBaseRoute
        path={routes.ACCOUNTS_EDIT()}
        component={EditAccount}
        permission={Permission.ACCESS_BASIC}
      />

      <CrumbRoute
        path={routes.ADVISOR_RANKINGS}
        component={Rankings}
        title={t('advisor_menu_RANKINGS')}
        permission={Permission.ACCESS_BASIC}
      />

      <CrumbRoute
        path={routes.ADMIN}
        component={Admin}
        title={t('admin_title')}
        permission={Permission.ACCESS_ADMIN}
      />

      <CrumbRoute
        path={routes.BACK_OFFICE}
        component={BackOffice}
        title={t('backOffice_title')}
        permission={Permission.ACCESS_BACKOFFICE}
      />

      <RoleBaseRoute
        path={routes.ACCOUNTS_COMPLETE()}
        component={AccountCompleteLayout}
        permission={Permission.ACCESS_BASIC}
      />

      <RoleBaseRoute
        path={routes.ACCOUNTS_DOCUMENTS()}
        component={DocumentsToBeSigned}
        permission={Permission.ACCESS_BASIC}
      />

      <RoleBaseRoute
        path={routes.ACCOUNTS_NOTES()}
        component={Notes}
        permission={Permission.ACCESS_BASIC}
      />

      <Route component={Page404} />
    </Switch>
  );
};

export default compose(inject('userStore'), observer)(AppRoutes);
